/**
 * /intl-breeders — Inbound International Breeder Directory.
 *
 * Public directory of foreign breeders who paid $199/yr to reach US
 * buyers + US breeders shopping for foundation stock. Venue only.
 */
import { isNativeApp, webOrigin } from "@/lib/nativeBridge";
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import PublicShell from "@/components/PublicShell";
import SeoHead from "@/components/SeoHead";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { GlobeHemisphereWestIcon as GlobeHemisphereWest, StorefrontIcon as Storefront, ArrowRightIcon as ArrowRight } from "@phosphor-icons/react";

const COUNTRY_OPTIONS = [
  { value: "any", label: "Any country" },
  { value: "CA", label: "Canada" },
  { value: "MX", label: "Mexico" },
  { value: "GB", label: "United Kingdom" },
  { value: "IE", label: "Ireland" },
  { value: "FR", label: "France" },
  { value: "DE", label: "Germany" },
  { value: "NL", label: "Netherlands" },
  { value: "ES", label: "Spain" },
  { value: "IT", label: "Italy" },
  { value: "PL", label: "Poland" },
  { value: "RO", label: "Romania" },
  { value: "RU", label: "Russia" },
  { value: "JP", label: "Japan" },
  { value: "KR", label: "South Korea" },
  { value: "AU", label: "Australia" },
  { value: "NZ", label: "New Zealand" },
];

export default function IntlBreeders() {
  const { user } = useAuth();
  const [country, setCountry] = useState("any");
  const [breed, setBreed] = useState("");
  const [shipsIntl, setShipsIntl] = useState(false);
  const [breedingRights, setBreedingRights] = useState(false);
  const [breeders, setBreeders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (country !== "any") params.set("country", country);
      if (breed.trim()) params.set("breed", breed.trim());
      if (shipsIntl) params.set("intl_shipping", "true");
      if (breedingRights) params.set("breeding_rights", "true");
      const r = await api.get(`/intl-breeders?${params.toString()}`);
      setBreeders(r.data?.breeders || []);
    } finally {
      setLoading(false);
    }
  }, [country, breed, shipsIntl, breedingRights]);
  useEffect(() => { load(); }, [load]);

  const Shell = user ? AppShell : PublicShell;
  return (
    <Shell>
      <SeoHead
        title="International Breeder Directory · Armstrong K9 Registry"
        description="Browse foreign kennels offering puppies and proven adults for US buyers. Email-relay handoffs only — Armstrong is the venue, you handle the transport."
        url={`${webOrigin()}/intl-breeders`}
        type="website"
      />
      <div className="max-w-6xl mx-auto px-4 py-10" data-testid="intl-breeders-page">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-3">
              International Breeder Directory
            </p>
            <h1 className="font-spectral font-light text-4xl sm:text-5xl tracking-tight leading-[1.05]">
              Foreign kennels, US-facing.
            </h1>
            <p className="text-sm text-[#6B6B6B] mt-3 max-w-2xl leading-relaxed">
              Vetted-by-payment kennels from outside the US listing themselves
              for US buyers and US breeders importing foundation stock. Every
              inquiry is an email handoff — Armstrong does not hold money or
              coordinate transport.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Link to="/intl-breeders/apply">
              <Button className="rounded-full bg-ink hover:bg-crimson text-white h-11 px-5" data-testid="intl-breeders-apply-cta">
                {isNativeApp() ? "List your kennel" : "List your kennel · $199/yr"}
              </Button>
            </Link>
            <Link to="/transport/international">
              <Button variant="outline" className="rounded-full border-[#E5E5E5] h-11" data-testid="intl-breeders-courier-link">
                Find a courier
              </Button>
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
          <aside className="bg-white border border-[#E5E5E5] rounded-xl p-5 space-y-4 h-fit">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] font-bold">Country</Label>
              <Select value={country} onValueChange={setCountry}>
                <SelectTrigger className="mt-2" data-testid="intl-breeders-country"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72 overflow-y-auto">
                  {COUNTRY_OPTIONS.map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.18em] font-bold">Breed</Label>
              <Input value={breed} onChange={(e) => setBreed(e.target.value)} placeholder="e.g. Cane Corso" className="mt-2" data-testid="intl-breeders-breed"/>
            </div>
            <label className="flex items-start gap-2 cursor-pointer text-sm">
              <input type="checkbox" checked={shipsIntl} onChange={(e) => setShipsIntl(e.target.checked)} className="mt-0.5 accent-[#C9A227]" data-testid="intl-breeders-ships-intl"/>
              <span>Ships internationally</span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer text-sm">
              <input type="checkbox" checked={breedingRights} onChange={(e) => setBreedingRights(e.target.checked)} className="mt-0.5 accent-[#C9A227]" data-testid="intl-breeders-breeding-rights"/>
              <span>Offers breeding rights</span>
            </label>
          </aside>

          <section>
            {loading && <p className="text-sm text-[#6B6B6B]">Loading…</p>}
            {!loading && breeders.length === 0 && (
              <div className="bg-[#FAFAFA] border border-[#E5E5E5] rounded-xl p-10 text-center" data-testid="intl-breeders-empty">
                <Storefront size={28} weight="duotone" className="mx-auto text-gold mb-2"/>
                <p className="font-semibold">No kennels yet for these filters.</p>
                <p className="text-sm text-[#6B6B6B] mt-1">Be the first foreign kennel to list — <Link to="/intl-breeders/apply" className="text-crimson underline">apply here</Link>.</p>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {breeders.map((b) => (
                <Link
                  key={b.id}
                  to={`/intl-breeders/${b.id}`}
                  className="block bg-white border border-[#EAEAEA] rounded-xl p-5 hover:border-gold/55 hover:-translate-y-0.5 hover:shadow-gold-glow transition-all"
                  data-testid={`intl-breeder-card-${b.id}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h3 className="font-spectral text-lg tracking-tight leading-tight flex-1">{b.kennel_name}</h3>
                    <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#6B6B6B] flex items-center gap-1">
                      <GlobeHemisphereWest size={12} weight="duotone"/>
                      {b.country}
                    </span>
                  </div>
                  {b.city && <p className="text-[12px] text-[#6B6B6B]">{b.city}</p>}
                  {b.breeds?.length > 0 && (
                    <p className="text-[13px] mt-2">
                      <span className="font-semibold">Breeds:</span> {b.breeds.slice(0, 4).join(", ")}{b.breeds.length > 4 ? "…" : ""}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {b.intl_shipping_supported && (
                      <span className="bg-[#EFF7EE] border border-[#0F8043]/40 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[#0F8043]">Ships intl</span>
                    )}
                    {b.breeding_rights_offered && (
                      <span className="bg-[#FBF7EC] border border-[#C9A227] rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[#8A6A00]">Breeding rights</span>
                    )}
                  </div>
                  {b.bio && (
                    <p className="text-[12.5px] text-[#6B6B6B] mt-3 line-clamp-2 leading-relaxed">{b.bio}</p>
                  )}
                  <span className="inline-flex items-center gap-1 text-[12px] text-crimson font-bold mt-3">
                    View kennel <ArrowRight size={11} weight="bold"/>
                  </span>
                </Link>
              ))}
            </div>
          </section>
        </div>
      </div>
    </Shell>
  );
}
